import { useEffect, useRef } from "react";
import { Control, Controller, useController, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import MonacoEditor, { loader, Monaco } from "@monaco-editor/react";
import { AlertCircle, HelpCircle } from "lucide-react";
import * as yup from "yup";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import ChipsMultiSelect, { ChipsMultiSelectOption } from "~/components/ui/chips-multi-select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { BaseInput } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useDebounceFalsy } from "~/hooks/useDebounceFalsy";
import { cn } from "~/lib/utils";
import * as monaco from "~/utils/monaco-editor";

loader.config({ monaco });

export type SkillTag = "ORCHESTRATION" | "ANALYSIS";

export interface SkillFormData {
  name: string;
  description: string;
  tags: SkillTag[];
  content: string;
}

export interface SkillFormErrors {
  name?: string;
  description?: string;
  tags?: string;
  content?: string;
  general?: string;
}

interface SkillEditorModalProps {
  errors?: SkillFormErrors;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (data: SkillFormData) => void | Promise<void>;
  open: boolean;
  initialData?: Partial<SkillFormData>;
  isEditing?: boolean;
}

const AVAILABLE_TAGS: ChipsMultiSelectOption<SkillTag>[] = [
  {
    value: "ORCHESTRATION",
    label: "Orchestration",
    description: "Skills that help coordinate and manage workflows",
  },
  {
    value: "ANALYSIS",
    label: "Analysis",
    description: "Skills that help analyze and interpret data",
  },
];

const DEFAULT_CONTENT = `When using this skill, always:

1. **First step**: Describe what to do first
2. **Second step**: Describe what to do next
3. **Third step**: Additional instructions

Keep responses clear and actionable.
`;

export const skillValidationSchema = yup.object({
  name: yup
    .string()
    .required("Skill name is required")
    .max(64, "Skill name must be 64 characters or less")
    .matches(/^[a-z0-9-]+$/, "Use only lowercase letters, numbers, and hyphens")
    .test("no-leading-hyphen", "Skill name cannot start with a hyphen", (value) =>
      value ? !value.startsWith("-") : true
    )
    .test("no-trailing-hyphen", "Skill name cannot end with a hyphen", (value) =>
      value ? !value.endsWith("-") : true
    ),
  description: yup.string().required("Description is required"),
  content: yup.string().required("Instructions are required"),
  tags: yup
    .array()
    .of(yup.mixed<SkillTag>().oneOf(["ORCHESTRATION", "ANALYSIS"]).required())
    .required()
    .default([]),
});

interface SkillNameFieldProps {
  control: Control<SkillFormData>;
  loading: boolean;
  isEditing: boolean;
}

const SkillNameField = ({ control, loading, isEditing }: SkillNameFieldProps) => {
  const { field, fieldState } = useController({ name: "name", control });
  // Delay error appearance to avoid flashing while typing; clear immediately
  const debouncedError = useDebounceFalsy(fieldState.error?.message, 0, 300);

  return (
    <BaseInput
      {...field}
      placeholder="my-skill-name"
      disabled={loading || isEditing}
      errorMessage={debouncedError}
      aria-invalid={!!debouncedError}
      data-1p-ignore
      topElement={
        <div className="flex items-center gap-1">
          <Label className="gap-0">
            Name <span className="text-destructive">*</span>
          </Label>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[220px] z-[1300]">
              Use lowercase letters, numbers, and hyphens. This is how you&apos;ll invoke the skill
              (e.g., /my-skill-name).
            </TooltipContent>
          </Tooltip>
        </div>
      }
    />
  );
};

const SkillEditorModal = ({
  errors = {},
  loading = false,
  onClose,
  onSubmit,
  open,
  initialData,
  isEditing = false,
}: SkillEditorModalProps) => {
  const monacoRef = useRef<Monaco | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isValid },
    reset,
  } = useForm<SkillFormData>({
    resolver: yupResolver(skillValidationSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      tags: [],
      content: DEFAULT_CONTENT,
    },
  });

  // Sync form when initialData prop changes (e.g., after async fetch) or when dialog opens
  useEffect(() => {
    if (open) {
      reset({
        name: initialData?.name ?? "",
        description: initialData?.description ?? "",
        tags: initialData?.tags ?? [],
        content: initialData?.content ?? DEFAULT_CONTENT,
      });
    }
  }, [open, initialData, reset]);

  const handleFormSubmit = (data: SkillFormData) => {
    onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[900px] sm:w-[calc(100%_-_64px)] p-0 gap-0 max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>{isEditing ? "Edit Skill" : "Add New Skill"}</DialogTitle>
        </DialogHeader>

        <div className="px-6 pt-4 pb-4 overflow-y-auto flex-1">
          {errors.general && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle />
              <AlertDescription>{errors.general}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {/* Name field */}
            <SkillNameField control={control} loading={loading} isEditing={isEditing} />

            {/* Description field */}
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="skill-description" className="gap-0">
                    Description <span className="text-destructive">*</span>
                  </Label>
                  <BaseInput
                    {...field}
                    id="skill-description"
                    placeholder="What this skill does and when to use it..."
                    disabled={loading}
                    errorMessage={fieldState.error?.message}
                    aria-invalid={!!fieldState.error}
                  />
                </div>
              )}
            />

            {/* Tags field */}
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <ChipsMultiSelect
                  label="Tags"
                  selectedValues={field.value}
                  options={AVAILABLE_TAGS}
                  onChange={field.onChange}
                  disabled={loading}
                />
              )}
            />

            {/* Content field */}
            <Controller
              name="content"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="skill-content" className="gap-0">
                    Instructions <span className="text-destructive">*</span>
                  </Label>
                  <div
                    className={cn(
                      "border rounded-md overflow-hidden",
                      fieldState.error && "border-destructive"
                    )}
                  >
                    <MonacoEditor
                      height="300px"
                      language="markdown"
                      value={field.value}
                      beforeMount={(m) => {
                        monacoRef.current = m;
                      }}
                      onChange={(v) => field.onChange(v ?? "")}
                      options={{
                        automaticLayout: true,
                        fixedOverflowWidgets: true,
                        formatOnPaste: true,
                        formatOnType: true,
                        minimap: { enabled: false },
                        readOnly: loading,
                        scrollBeyondLastLine: false,
                        wordBasedSuggestions: "off",
                        wordWrap: "on",
                      }}
                    />
                  </div>
                  {fieldState.error && (
                    <span className="text-xs text-destructive">{fieldState.error.message}</span>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        <DialogFooter className="px-6 pb-4 shrink-0">
          <Button variant="outline" onClick={onClose} disabled={loading} size="sm">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            disabled={!isValid || loading}
            loading={loading}
            size="sm"
            className="min-w-[72px]"
          >
            {isEditing ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SkillEditorModal;
